import React, { useState, useEffect, FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { PageProps, User } from "../types/auth";

import styles from './styles/CreateInvitePage.module.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Organization {
  org_id: number;
  name: string;
}

const generateInviteCode = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const length = 16;
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const CreateInvitePage: React.FC<PageProps> = ({ user, token, selectedOrgID }) => {
  const [code] = useState<string>(generateInviteCode());
  const [email, setEmail] = useState<string>("");
  const [orgID, setOrgID] = useState<number>(user?.organizations?.[0]?.org_id || 0);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [isOwner, setIsOwner] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [isSending, setIsSending] = useState<boolean>(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/organizations`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Org-ID": selectedOrgID?.toString() || "",
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.error || "Failed to fetch organizations.");
          return;
        }

        const data = await response.json();
        setOrganizations(data);

        // If no orgID is set, default to the first organization in the list
        if (!orgID && data.length > 0) {
          setOrgID(data[0].org_id);
        }
      } catch (err) {
        setError("An unexpected error occurred while fetching organizations.");
      }
    };

    fetchOrganizations();
  }, [token, orgID, selectedOrgID]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setIsSending(true);
    try {
      const orgIDToSend = user?.sa ? orgID : user?.organizations?.[0]?.org_id;
      if (!orgIDToSend || orgIDToSend <= 0) {
        setError("No organization found for this user.");
        setIsSending(false);
        return;
      }
      const response = await fetch(`${API_BASE_URL}/api/invites`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Org-ID": selectedOrgID?.toString() || "",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code,
          org_id: orgIDToSend,
          is_owner: isOwner,
          sent_to_email: email
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Failed to create invite.");
        setIsSending(false);
        return;
      }

      // Navigate back to invites page
      navigate("/invites");
    } catch (err) {
      setError("An unexpected error occurred.");
      setIsSending(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Create Invite</h1>
      {error && <p className={styles.errorMessage}>{error}</p>}
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.label}>Send to:</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter recipient email address"
            required
            className={styles.input}
          />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.label}>Code:</label>
          <input
            type="text"
            value={code}
            readOnly
            className={styles.readonlyInput}
          />
        </div>
        {user?.sa && (
          <>
            <div className={styles.formGroup}>
              <label className={styles.label}>Organization:</label>
              <select
                value={orgID}
                onChange={(e) => setOrgID(Number(e.target.value))}
                required
                className={styles.select}
              >
                {organizations.map((org) => (
                  <option key={org.org_id} value={org.org_id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.checkboxGroup}>
              <input
                type="checkbox"
                checked={isOwner}
                onChange={(e) => setIsOwner(e.target.checked)}
                className={styles.checkbox}
                id="isOwner"
              />
              <label htmlFor="isOwner" className={styles.label}>Is Owner</label>
            </div>
          </>
        )}
        <button 
          type="submit" 
          disabled={isSending}
          className={styles.submitButton}
        >
          {isSending ? "Sending..." : "Send"}
        </button>
      </form>
    </div>
  );
};

export default CreateInvitePage;
